import {
  COMPANY_SIZE_OPTIONS,
  INDUSTRY_OPTIONS,
  REFERRAL_SOURCE_OPTIONS,
  ROLE_OPTIONS,
  SELECT_INPUT_OTHER_VALUE,
  TEAM_OPTIONS
} from "@/src/common/business-layer/constants"
import { SelectOption, SimpleSelect } from "@/src/common/components/Select"
import Button from "@/src/sb/components/Button"
import { TextInput } from "@/src/sb/components/Input"
import { CitySelect, DialingCodeSelect } from "@/src/sb/components/auth/fields"
import {
  AccountDetails,
  getAccountDetailsSchema
} from "@/src/sb/core/validatorSchemas/profile-setup"
import { useFormik } from "formik"
import { useEffect, useMemo } from "react"
import { ValidationError } from "yup"

import Field from "./Field"
import {
  EMBEDDED_SELECT_CLASS_NAMES,
  PHONE_DIVIDER_CLASSES,
  PHONE_DIVIDER_WRAPPER_CLASSES,
  SELECT_CLASS_NAMES,
  SPECIFY_INPUT_CLASSES,
  TEXT_INPUT_CLASSES
} from "./fieldStyles"

export const EMPTY_ACCOUNT_DETAILS: AccountDetails = {
  role: "",
  roleOther: "",
  team: "",
  teamOther: "",
  dialingCode: "",
  phoneNumber: "",
  city: "",
  country: "",
  companyName: "",
  industry: "",
  industryOther: "",
  companySize: "",
  referralSource: ""
}

const getValidationErrors = (
  values: AccountDetails,
  includeOrganisation: boolean
) => {
  try {
    getAccountDetailsSchema(includeOrganisation).validateSync(values, {
      abortEarly: false
    })
    return {} as Partial<Record<keyof AccountDetails, string>>
  } catch (error) {
    return ((error as ValidationError).inner ?? []).reduce(
      (errors, { path, message }) =>
        path && !errors[path as keyof AccountDetails]
          ? { ...errors, [path]: message }
          : errors,
      {} as Partial<Record<keyof AccountDetails, string>>
    )
  }
}

type AccountDetailsStepProps = {
  initialValues?: AccountDetails
  onSubmit: (values: AccountDetails) => void
  onChange?: (values: AccountDetails) => void
  includeOrganisation?: boolean
  serverErrors?: Partial<Record<keyof AccountDetails, string>>
}

const AccountDetailsStep = ({
  initialValues = EMPTY_ACCOUNT_DETAILS,
  onSubmit,
  onChange,
  includeOrganisation = false,
  serverErrors
}: AccountDetailsStepProps) => {
  const form = useFormik<AccountDetails>({ initialValues, onSubmit })

  useEffect(() => {
    onChange?.(form.values)
  }, [form.values])

  const validationErrors = useMemo(
    () => getValidationErrors(form.values, includeOrganisation),
    [form.values, includeOrganisation]
  )
  const formIsComplete = Object.keys(validationErrors).length === 0

  const errorsFor = (field: keyof AccountDetails) => {
    const serverError = serverErrors?.[field]
    if (serverError) return [serverError]

    return form.touched[field] && validationErrors[field]
      ? [validationErrors[field] as string]
      : []
  }

  return (
    <form
      onSubmit={form.handleSubmit}
      noValidate
      className="flex flex-col gap-5"
    >
      <div className="flex flex-col gap-4">
        {includeOrganisation ? (
          <>
            <Field label="Company Name" htmlFor="companyName">
              <TextInput
                id="companyName"
                name="companyName"
                type="text"
                placeholder="Enter company name"
                value={form.values.companyName ?? ""}
                extraInputClasses={SPECIFY_INPUT_CLASSES}
                errorShiftCorrected={false}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
                errors={errorsFor("companyName")}
              />
            </Field>

            <Field label="Industry" htmlFor="industry">
              <SimpleSelect
                inputId="industry"
                name="industry"
                isSearchable={false}
                placeholder="Select industry"
                options={INDUSTRY_OPTIONS}
                classNames={SELECT_CLASS_NAMES}
                value={
                  form.values.industry
                    ? {
                        value: form.values.industry,
                        label: form.values.industry
                      }
                    : null
                }
                onChange={(selectedOption: SelectOption) =>
                  form.setFieldValue("industry", selectedOption.value)
                }
                onBlur={form.handleBlur}
                errors={errorsFor("industry")}
              />
            </Field>

            {form.values.industry === SELECT_INPUT_OTHER_VALUE ? (
              <Field
                label="Please specify your industry"
                htmlFor="industryOther"
              >
                <TextInput
                  id="industryOther"
                  name="industryOther"
                  type="text"
                  autoFocus
                  placeholder="Enter industry name"
                  value={form.values.industryOther ?? ""}
                  extraInputClasses={SPECIFY_INPUT_CLASSES}
                  errorShiftCorrected={false}
                  onChange={form.handleChange}
                  onBlur={form.handleBlur}
                  errors={errorsFor("industryOther")}
                />
              </Field>
            ) : null}

            <Field label="Number of Employees" htmlFor="companySize">
              <SimpleSelect
                inputId="companySize"
                name="companySize"
                isSearchable={false}
                placeholder="Select number of employees"
                options={COMPANY_SIZE_OPTIONS}
                classNames={SELECT_CLASS_NAMES}
                value={
                  form.values.companySize
                    ? {
                        value: form.values.companySize,
                        label: form.values.companySize
                      }
                    : null
                }
                onChange={(selectedOption: SelectOption) =>
                  form.setFieldValue("companySize", selectedOption.value)
                }
                onBlur={form.handleBlur}
                errors={errorsFor("companySize")}
              />
            </Field>
          </>
        ) : null}

        <Field label="Role" htmlFor="role">
          <SimpleSelect
            inputId="role"
            name="role"
            isSearchable={false}
            placeholder="Select role"
            options={ROLE_OPTIONS}
            classNames={SELECT_CLASS_NAMES}
            value={
              form.values.role
                ? { value: form.values.role, label: form.values.role }
                : null
            }
            onChange={(selectedOption: SelectOption) =>
              form.setFieldValue("role", selectedOption.value)
            }
            onBlur={form.handleBlur}
            errors={errorsFor("role")}
          />
        </Field>

        {form.values.role === SELECT_INPUT_OTHER_VALUE ? (
          <Field label="Please specify your role" htmlFor="roleOther">
            <TextInput
              id="roleOther"
              name="roleOther"
              type="text"
              autoFocus
              placeholder="Enter role name"
              value={form.values.roleOther ?? ""}
              extraInputClasses={SPECIFY_INPUT_CLASSES}
              errorShiftCorrected={false}
              onChange={form.handleChange}
              onBlur={form.handleBlur}
              errors={errorsFor("roleOther")}
            />
          </Field>
        ) : null}

        <Field label="Team" htmlFor="team">
          <SimpleSelect
            inputId="team"
            name="team"
            isSearchable={false}
            placeholder="Select team"
            options={TEAM_OPTIONS}
            classNames={SELECT_CLASS_NAMES}
            value={
              form.values.team
                ? { value: form.values.team, label: form.values.team }
                : null
            }
            onChange={(selectedOption: SelectOption) =>
              form.setFieldValue("team", selectedOption.value)
            }
            onBlur={form.handleBlur}
            errors={errorsFor("team")}
          />
        </Field>

        {form.values.team === SELECT_INPUT_OTHER_VALUE ? (
          <Field label="Please specify your team" htmlFor="teamOther">
            <TextInput
              id="teamOther"
              name="teamOther"
              type="text"
              autoFocus
              placeholder="Enter team name"
              value={form.values.teamOther ?? ""}
              extraInputClasses={SPECIFY_INPUT_CLASSES}
              errorShiftCorrected={false}
              onChange={form.handleChange}
              onBlur={form.handleBlur}
              errors={errorsFor("teamOther")}
            />
          </Field>
        ) : null}

        <Field label="Phone Number" htmlFor="phoneNumber">
          <TextInput
            id="phoneNumber"
            name="phoneNumber"
            type="text"
            value={form.values.phoneNumber}
            leftReactNode={
              <>
                <DialingCodeSelect
                  value={form.values.dialingCode}
                  placeholder="Country code"
                  className=""
                  classNames={EMBEDDED_SELECT_CLASS_NAMES}
                  hasError={Boolean(errorsFor("phoneNumber").length)}
                  onChange={(dialingCode) =>
                    form.setFieldValue("dialingCode", dialingCode)
                  }
                />
                <span aria-hidden className={PHONE_DIVIDER_WRAPPER_CLASSES}>
                  <span className={PHONE_DIVIDER_CLASSES} />
                </span>
              </>
            }
            extraInputClasses={TEXT_INPUT_CLASSES}
            errorShiftCorrected={false}
            onChange={form.handleChange}
            onBlur={form.handleBlur}
            errors={errorsFor("phoneNumber")}
          />
        </Field>

        <Field label="City" htmlFor="city">
          <CitySelect
            inputId="city"
            value={form.values.city}
            classNames={SELECT_CLASS_NAMES}
            onChange={({ city, country }) =>
              form.setValues({ ...form.values, city, country })
            }
            onBlur={form.handleBlur}
            errors={errorsFor("city")}
          />
        </Field>

        {includeOrganisation ? (
          <Field label="How did you hear about us" htmlFor="referralSource">
            <SimpleSelect
              inputId="referralSource"
              name="referralSource"
              isSearchable={false}
              placeholder="Select"
              options={REFERRAL_SOURCE_OPTIONS}
              classNames={SELECT_CLASS_NAMES}
              value={
                form.values.referralSource
                  ? {
                      value: form.values.referralSource,
                      label: form.values.referralSource
                    }
                  : null
              }
              onChange={(selectedOption: SelectOption) =>
                form.setFieldValue("referralSource", selectedOption.value)
              }
              onBlur={form.handleBlur}
              errors={errorsFor("referralSource")}
            />
          </Field>
        ) : null}
      </div>

      <Button
        type="primary"
        roundness="edge"
        stretch
        htmlType="submit"
        disabled={!formIsComplete}
        extraClassNames="dark:bg-[#4B5563] dark:h-[38px]"
      >
        Next
      </Button>
    </form>
  )
}

export default AccountDetailsStep
